'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdVpnKey,
    MdClose,
    MdCheckCircle,
    MdCancel,
    MdArrowBack,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getTissCredentials,
    deleteTissCredential,
    TissCredential,
} from '@/lib/api/health-integrations/tiss-api'

/**
 * Credenciais de transmissão TISS desta filial.
 *
 * Não é o cadastro de operadoras — quem é a operadora (nome, registro ANS) vem do
 * convênio, cadastrado em /medical/health-plans e compartilhado por toda a
 * organização. Aqui mora só o acesso ao webservice de cada uma.
 */
export default function TissCredentialsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'health_integrations',
        'tiss'
    )

    const [credentials, setCredentials] = useState<TissCredential[]>([])
    const [filteredCredentials, setFilteredCredentials] = useState<
        TissCredential[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [credentialToDelete, setCredentialToDelete] = useState<number | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadCredentials()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase()
            const filtered = credentials.filter(
                credential =>
                    credential.healthPlan?.name
                        ?.toLowerCase()
                        .includes(term) ||
                    credential.healthPlan?.ansCode
                        ?.toLowerCase()
                        .includes(term)
            )
            setFilteredCredentials(filtered)
        } else {
            setFilteredCredentials(credentials)
        }
    }, [searchTerm, credentials])

    const loadCredentials = async () => {
        setLoading(true)
        try {
            const response = await getTissCredentials()
            if (response.success) {
                setCredentials(response.result)
                setFilteredCredentials(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar credenciais')
            }
        } catch (error) {
            toast.error('Erro ao carregar credenciais')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setCredentialToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!credentialToDelete) return

        setDeleting(true)
        try {
            const response = await deleteTissCredential(credentialToDelete)
            if (response.success) {
                toast.success('Credencial excluída com sucesso')
                loadCredentials()
                setShowDeleteModal(false)
                setCredentialToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir credencial')
            }
        } catch (error) {
            toast.error('Erro ao excluir credencial')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando credenciais...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/health-integrations/tiss')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                            <MdVpnKey className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-base-content">
                                Credenciais de transmissão
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {credentials.length}{' '}
                                {credentials.length === 1
                                    ? 'convênio configurado'
                                    : 'convênios configurados'}{' '}
                                nesta filial
                            </p>
                        </div>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() =>
                            navigate(
                                '/health-integrations/tiss/credentials/new'
                            )
                        }
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Buscar por convênio ou código ANS..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredCredentials.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdVpnKey className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma credencial encontrada'
                                : 'Nenhuma credencial cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Configure o acesso ao webservice dos convênios com que esta filial transmite'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate(
                                        '/health-integrations/tiss/credentials/new'
                                    )
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar credencial
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Convênio</th>
                                    <th>Código ANS</th>
                                    <th>Webservice</th>
                                    <th>Transmissões</th>
                                    <th>Status</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCredentials.map(credential => (
                                    <tr key={credential.id} className="hover">
                                        <td className="font-medium">
                                            {credential.healthPlan?.name ||
                                                `Convênio #${credential.healthPlanId}`}
                                        </td>
                                        <td>
                                            {credential.healthPlan?.ansCode ||
                                                '-'}
                                        </td>
                                        <td
                                            className="max-w-xs truncate"
                                            title={credential.webServiceUrl}
                                        >
                                            {credential.webServiceUrl || '-'}
                                        </td>
                                        <td>
                                            {credential._count?.transactions ??
                                                0}
                                        </td>
                                        <td>
                                            {credential.active ? (
                                                <span className="badge badge-success badge-sm gap-1">
                                                    <MdCheckCircle className="text-xs" />
                                                    Ativa
                                                </span>
                                            ) : (
                                                <span className="badge badge-ghost badge-sm gap-1">
                                                    <MdCancel className="text-xs" />
                                                    Inativa
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {(canUpdate || canDelete) && (
                                                <div className="flex items-center gap-1">
                                                    {canUpdate && (
                                                        <button
                                                            onClick={() =>
                                                                navigate(
                                                                    `/health-integrations/tiss/credentials/edit/${credential.id}`
                                                                )
                                                            }
                                                            className="btn btn-ghost btn-sm"
                                                            title="Editar"
                                                        >
                                                            <MdEdit className="text-lg" />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            onClick={() =>
                                                                handleDelete(
                                                                    credential.id
                                                                )
                                                            }
                                                            className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                                                            title="Excluir"
                                                        >
                                                            <MdDelete className="text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setCredentialToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir credencial"
                message="Tem certeza de que deseja excluir esta credencial de transmissão? As transmissões já feitas com ela são mantidas."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
